/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import { Box, Divider, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import { Invocations } from "@/Actions/components";
import AdminOverview from "@/Settings/AdminOverview";
import {
  PageLayout,
  RobotoStyledTab,
  RobotoStyledTabs,
  RobotoTabPanel,
} from "@/shared/components";
import { SystemUserOnly } from "@/shared/components/SystemUserOnly";

import { BYOB } from "./BYOB";
import { Config } from "./Config";
import { ExploreAgentThreads } from "./ExploreAgentThreads";
import { FeatureFlagsPage } from "./FeatureFlags/FeatureFlagsPage";
import { McpServers } from "./McpServers";
import { Migrations } from "./Migrations";
import { Orgs } from "./Orgs";
import { PerActionStats } from "./PerActionStats";
import { RestExplorer } from "./RestExplorer";

export function Admin() {
  const theme = useTheme();

  const [searchParams, setSearchParams] = useSearchParams();

  const tabSearchParam = searchParams.get("tab");
  const tabValue = tabSearchParam !== null ? parseInt(tabSearchParam) : 0;

  return (
    <SystemUserOnly>
      <PageLayout title={"Roboto - Admin Console"} headerContentLeft="Admin">
        <Box
          sx={{
            paddingTop: theme.spacing(0.5),
          }}
        >
          <RobotoStyledTabs
            value={tabValue}
            onChange={(_event, newValue) => {
              setSearchParams({ tab: String(newValue) });
            }}
            aria-label="settings tabs"
          >
            <RobotoStyledTab label="Overview" />
            <RobotoStyledTab label="Orgs" />
            <RobotoStyledTab label="Invocations" />
            <RobotoStyledTab label="Migrations" />
            <RobotoStyledTab label="Config" />
            <RobotoStyledTab label="Action Stats" />
            <RobotoStyledTab label="REST Explorer" />
            <RobotoStyledTab label="BYOB" />
            <RobotoStyledTab label="Feature Flags" />
            <RobotoStyledTab label="MCP Servers" />
            <RobotoStyledTab label="Explore Threads" />
          </RobotoStyledTabs>

          <Divider
            sx={{
              borderColor: theme.border.color,
            }}
          />
        </Box>

        <Box
          sx={{
            padding: theme.spacing(2, 0),
            paddingBottom: theme.spacing(1),
          }}
        >
          <RobotoTabPanel
            index={0}
            value={tabValue}
            sx={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
            }}
          >
            <AdminOverview />
          </RobotoTabPanel>

          <RobotoTabPanel
            index={1}
            value={tabValue}
            sx={{
              paddingBottom: theme.spacing(1),
            }}
          >
            <Orgs />
          </RobotoTabPanel>
          <RobotoTabPanel
            index={2}
            value={tabValue}
            sx={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
            }}
          >
            <Invocations currentOrg={null} />
          </RobotoTabPanel>
          <RobotoTabPanel
            index={3}
            value={tabValue}
            sx={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
            }}
          >
            <Migrations />
          </RobotoTabPanel>
          <RobotoTabPanel
            index={4}
            value={tabValue}
            sx={{
              padding: theme.spacing(2),
              paddingBottom: theme.spacing(1),
            }}
          >
            <Config />
          </RobotoTabPanel>
          <RobotoTabPanel
            index={5}
            value={tabValue}
            sx={{
              padding: theme.spacing(2),
              paddingBottom: theme.spacing(1),
            }}
          >
            <PerActionStats />
          </RobotoTabPanel>
          <RobotoTabPanel
            index={6}
            value={tabValue}
            sx={{
              padding: theme.spacing(2),
              paddingBottom: theme.spacing(1),
            }}
          >
            <RestExplorer />
          </RobotoTabPanel>
          <RobotoTabPanel
            index={7}
            value={tabValue}
            sx={{
              padding: theme.spacing(2),
              paddingBottom: theme.spacing(1),
            }}
          >
            <BYOB />
          </RobotoTabPanel>
          <RobotoTabPanel
            index={8}
            value={tabValue}
            sx={{
              padding: theme.spacing(2),
              paddingBottom: theme.spacing(1),
            }}
          >
            <FeatureFlagsPage />
          </RobotoTabPanel>
          <RobotoTabPanel
            index={9}
            value={tabValue}
            sx={{
              padding: theme.spacing(2),
              paddingBottom: theme.spacing(1),
            }}
          >
            <McpServers />
          </RobotoTabPanel>
          <RobotoTabPanel
            index={10}
            value={tabValue}
            sx={{
              padding: theme.spacing(2),
              paddingBottom: theme.spacing(1),
            }}
          >
            <ExploreAgentThreads />
          </RobotoTabPanel>
        </Box>
      </PageLayout>
    </SystemUserOnly>
  );
}
